import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Calendar,
  Clock,
  Hash,
  MapPin,
  RefreshCw,
  Users,
  UserRound,
} from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import { Service } from "../../../models/ServiceModel";
import useServicesService from "../../../services/useServicesService";

type ServiceOption = { value: string; label: string };

type LookupMode = "id" | "enaex_code";

type Props = {
  value: string;
  companyId?: number | null;
  serviceDate?: string | null;
  excludeIds?: number[];
  disabled?: boolean;
  /** ENAEX: permite buscar por ID o código fuera del día filtrado. */
  allowIdSearch?: boolean;
  onChange: (serviceId: string, service?: Service | null) => void;
};

const formatDate = (raw?: string | null) => {
  if (!raw) return "—";
  return String(raw).slice(0, 10);
};

const formatTime = (raw?: string | null) => {
  if (!raw) return "—";
  return String(raw).slice(0, 5);
};

const formatServiceLabel = (service: Service) => {
  const date = formatDate(service.service_date);
  const time = formatTime(service.service_time);
  const origin = service.origin_name || "Sin origen";
  const dest = service.destination_name || "Sin destino";
  const pax = service.passenger || "—";
  const code = (service.enaex_code || "").trim();
  const codePart = code ? ` · code ${code}` : "";
  return `#${service.id}${codePart} · ${date} ${time} · ${origin} → ${dest} (${pax} pax)`;
};

type PassengerDetail = { id?: number | null; name?: string; address?: string };

const getPassengerDetails = (service?: Service | null): PassengerDetail[] => {
  if (!service) return [];
  const details = service.passenger_details;
  if (Array.isArray(details) && details.length > 0) {
    return details.map((item) => ({
      id: item.id ?? null,
      name: (item.name || "Pasajero sin nombre").trim(),
      address: (item.address || "").trim(),
    }));
  }
  return [];
};

const passengerCountLabel = (service?: Service | null): string => {
  if (!service) return "—";
  const details = getPassengerDetails(service);
  if (details.length > 0) return String(details.length);
  if (typeof service.passenger === "number") return String(service.passenger);
  if (Array.isArray(service.passengers) && service.passengers.length) {
    return String(service.passengers.length);
  }
  return "—";
};

const ServicePickerSelect: React.FC<Props> = ({
  value,
  companyId,
  serviceDate,
  excludeIds = [],
  disabled = false,
  allowIdSearch = false,
  onChange,
}) => {
  const { list } = useServicesService();
  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [servicesById, setServicesById] = useState<Record<string, Service>>({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lookupActive, setLookupActive] = useState(false);
  const [lookupMode, setLookupMode] = useState<LookupMode | null>(null);
  const [lookupTerm, setLookupTerm] = useState("");
  const [editing, setEditing] = useState(false);
  const requestRef = useRef<AbortController | null>(null);
  const lastAutoSelectRef = useRef<string>("");
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const excludeKey = useMemo(() => excludeIds.join(","), [excludeIds]);

  /** Para ENAEX: ID numérico o código (texto / alfanumérico). */
  const remoteLookup = useMemo(() => {
    if (!allowIdSearch) return null;
    const raw = searchQuery.trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) {
      return { mode: "id" as const, term: raw };
    }
    if (raw.length >= 2) {
      return { mode: "enaex_code" as const, term: raw };
    }
    return null;
  }, [allowIdSearch, searchQuery]);

  const selected = value ? servicesById[value] ?? null : null;
  const showPicker = !value || editing;

  const loadServices = useCallback(
    (lookup?: { mode: LookupMode; term: string } | null) => {
      requestRef.current?.abort();
      const ctrl = new AbortController();
      requestRef.current = ctrl;
      setLoading(true);
      setLookupActive(Boolean(lookup));
      setLookupMode(lookup?.mode ?? null);
      setLookupTerm(lookup?.term ?? "");

      const filters: Record<string, string | number | boolean> = {
        page: 1,
        page_size: 200,
        general_mode: "true",
      };
      if (companyId) filters.company_id = companyId;

      if (lookup?.mode === "id") {
        filters.id = lookup.term;
      } else if (lookup?.mode === "enaex_code") {
        filters.enaex_code = lookup.term;
      } else if (serviceDate) {
        filters.start_date = serviceDate.slice(0, 10);
        filters.end_date = serviceDate.slice(0, 10);
      }

      list(
        filters,
        {
          onSuccess: (resp: any) => {
            if (ctrl.signal.aborted) return;
            const items: Service[] = Array.isArray(resp?.data?.services)
              ? resp.data.services
              : [];
            const excluded = new Set(excludeIds.map((id) => String(id)));

            // Si busca por ID y no hay match, reintentar como código ENAEX.
            if (lookup?.mode === "id" && items.length === 0 && allowIdSearch) {
              loadServices({ mode: "enaex_code", term: lookup.term });
              return;
            }

            setServicesById((prev) => {
              const next = { ...prev };
              items.forEach((service) => {
                if (service.id) next[String(service.id)] = service;
              });
              return next;
            });

            const mapped = items
              .filter((service) => service.id && !excluded.has(String(service.id)))
              .map((service) => ({
                value: String(service.id),
                label: formatServiceLabel(service),
              }));

            if (value && !mapped.some((opt) => opt.value === value)) {
              const selectedItem = items.find((service) => String(service.id) === value);
              if (selectedItem?.id && !excluded.has(String(selectedItem.id))) {
                mapped.unshift({
                  value: String(selectedItem.id),
                  label: formatServiceLabel(selectedItem),
                });
              } else if (value) {
                mapped.unshift({
                  value,
                  label: `#${value} · servicio seleccionado`,
                });
              }
            }

            setOptions(mapped);
            setLoading(false);

            if (
              lookup?.mode === "id" &&
              mapped.length === 1 &&
              mapped[0].value === lookup.term &&
              lastAutoSelectRef.current !== `id:${lookup.term}`
            ) {
              lastAutoSelectRef.current = `id:${lookup.term}`;
              const found = items.find((s) => String(s.id) === lookup.term) ?? null;
              onChangeRef.current(mapped[0].value, found);
              setEditing(false);
            }
          },
          onError: () => {
            if (ctrl.signal.aborted) return;
            setOptions([]);
            setLoading(false);
          },
        },
        ctrl.signal
      );
    },
    [allowIdSearch, companyId, excludeIds, list, serviceDate, value]
  );

  useEffect(() => {
    loadServices();
    return () => requestRef.current?.abort();
  }, [companyId, serviceDate, excludeKey, loadServices]);

  useEffect(() => {
    if (!allowIdSearch) return;
    if (!remoteLookup) {
      if (lookupActive) loadServices(null);
      return;
    }
    const timeout = window.setTimeout(() => loadServices(remoteLookup), 280);
    return () => window.clearTimeout(timeout);
  }, [allowIdSearch, remoteLookup, lookupActive, loadServices]);

  useEffect(() => {
    if (!value) setEditing(false);
  }, [value]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) lastAutoSelectRef.current = "";
  }, []);

  const handleSelect = (serviceId: string) => {
    const service = servicesById[serviceId] ?? null;
    onChange(serviceId, service);
    setEditing(false);
  };

  const placeholder = loading
    ? lookupActive
      ? lookupMode === "enaex_code"
        ? "Buscando por código ENAEX…"
        : "Buscando por ID…"
      : "Cargando servicios…"
    : allowIdSearch
      ? "Selecciona, ID o código ENAEX"
      : "Selecciona un servicio";

  const searchPlaceholder = allowIdSearch
    ? "ID, código ENAEX, origen…"
    : "Buscar servicio…";

  if (!showPicker && value) {
    const enaexCode = (selected?.enaex_code || "").trim();
    return (
      <article className="service-picker-card" aria-label={`Servicio #${value}`}>
        <header className="service-picker-card__header">
          <div className="service-picker-card__id">
            <span className="service-picker-card__badge">#{value}</span>
            {enaexCode ? (
              <span className="service-picker-card__code" title="Código ENAEX">
                {enaexCode}
              </span>
            ) : null}
            {selected?.status ? (
              <span className="service-picker-card__status">{String(selected.status)}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="service-picker-card__change"
            onClick={() => setEditing(true)}
            disabled={disabled}
          >
            <RefreshCw size={13} strokeWidth={2.25} aria-hidden />
            Cambiar
          </button>
        </header>

        <div className="service-picker-card__meta">
          <span>
            <Calendar size={13} strokeWidth={2} aria-hidden />
            {formatDate(selected?.service_date)}
          </span>
          <span>
            <Clock size={13} strokeWidth={2} aria-hidden />
            {formatTime(selected?.service_time)}
          </span>
          <span>
            <Users size={13} strokeWidth={2} aria-hidden />
            {passengerCountLabel(selected)} pax
          </span>
        </div>

        <div className="service-picker-card__route">
          <div className="service-picker-card__place">
            <MapPin size={14} strokeWidth={2} aria-hidden />
            <div>
              <small>Origen</small>
              <strong title={selected?.origin_name || undefined}>
                {selected?.origin_name || "Sin origen"}
              </strong>
            </div>
          </div>
          <ArrowRight className="service-picker-card__arrow" size={16} aria-hidden />
          <div className="service-picker-card__place">
            <MapPin size={14} strokeWidth={2} aria-hidden />
            <div>
              <small>Destino</small>
              <strong title={selected?.destination_name || undefined}>
                {selected?.destination_name || "Sin destino"}
              </strong>
            </div>
          </div>
        </div>

        <div className="service-picker-card__people">
          <span>
            <UserRound size={13} strokeWidth={2} aria-hidden />
            Conductor: <strong>{selected?.driver_name || "Sin asignar"}</strong>
          </span>
          {(selected?.authorizer_name || selected?.requester_name) && (
            <span>
              {selected?.authorizer_name
                ? `Autoriza: ${selected.authorizer_name}`
                : `Solicita: ${selected.requester_name}`}
            </span>
          )}
        </div>

        {(() => {
          const details = getPassengerDetails(selected);
          if (details.length > 0) {
            return (
              <div className="service-picker-card__pax-list">
                <div className="service-picker-card__pax-list-title">
                  <Users size={13} strokeWidth={2} aria-hidden />
                  Pasajeros ({details.length})
                </div>
                <ul>
                  {details.map((pax, index) => (
                    <li key={pax.id ?? `${pax.name}-${index}`}>
                      <strong>{pax.name || "Pasajero sin nombre"}</strong>
                      <span title={pax.address || undefined}>
                        <MapPin size={12} strokeWidth={2} aria-hidden />
                        {pax.address || "Sin dirección"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          if (typeof selected?.passenger === "string" && selected.passenger.trim()) {
            return (
              <p className="service-picker-card__passengers" title={selected.passenger}>
                {selected.passenger}
              </p>
            );
          }
          return (
            <p className="service-picker-card__passengers service-picker-card__passengers--empty">
              Sin detalle de pasajeros
            </p>
          );
        })()}
      </article>
    );
  }

  return (
    <div className={`service-picker-select${allowIdSearch ? " is-enaex" : ""}`}>
      {value && editing ? (
        <div className="service-picker-editing-bar">
          <span>Cambiando servicio #{value}</span>
          <button
            type="button"
            className="service-picker-card__change"
            onClick={() => setEditing(false)}
          >
            Cancelar
          </button>
        </div>
      ) : null}
      <BaseSelect
        label="Servicio"
        hideLabel
        name="service_picker"
        value={value}
        placeholder={placeholder}
        searchable
        searchPlaceholder={searchPlaceholder}
        preserveCase
        size="compact"
        menuMinWidth={420}
        includePlaceholderOption={false}
        disabled={disabled || loading}
        options={options}
        onSearchChange={handleSearchChange}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
          handleSelect(event.target.value)
        }
      />
      {allowIdSearch && (
        <p className="service-picker-hint">
          <Hash size={12} strokeWidth={2.25} aria-hidden />
          ENAEX: busca por ID o código, aunque el servicio no sea del día.
        </p>
      )}
      {!loading && lookupActive && lookupTerm && options.length === 0 && (
        <p className="service-picker-hint service-picker-hint--warn">
          {lookupMode === "enaex_code"
            ? `No hay servicio con código ENAEX “${lookupTerm}”`
            : `No hay servicio con ID ${lookupTerm}`}
          {companyId ? " para esta empresa" : ""}.
        </p>
      )}
    </div>
  );
};

export default ServicePickerSelect;
